// ============================================================
// useStage - what the table is looking at, for every member.
//
// One listener on `shared/stage`, and the title card's beat: a card is
// shown for TITLE_CARD_MS from `cutAt`, which every client reads from
// the same document so the beat starts at one moment for the whole
// table. One timer, armed at the end of the beat, re-evaluates the card;
// nothing polls (v8.337.0).
// ============================================================
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { cardFor, emptyStage, TITLE_CARD_MS } from '../engine/scenes';

export default function useStage({ sessionId, mp }) {
  const [stage, setStageState] = useState(null);
  const stageRef = useRef(stage);
  stageRef.current = stage;

  useEffect(() => {
    if (!sessionId) return undefined;
    return mp.onStageUpdate(sessionId, (doc) => setStageState(doc ? { ...emptyStage(), ...doc } : null));
  }, [sessionId, mp]);

  const now = useBeatClock(stage?.cutAt ?? null);
  const card = useMemo(() => cardFor(stage, now), [stage, now]);

  /** Write the stage through, and show it at once. Accepts a value or an updater over the current one. */
  const setStage = useCallback(
    (next) => {
      const value = typeof next === 'function' ? next(stageRef.current ?? emptyStage()) : next;
      setStageState(value);
      if (sessionId) mp.setStage(sessionId, value).catch(console.error);
    },
    [sessionId, mp],
  );

  return { stage, card, setStage };
}

/**
 * The clock the title card is read against: fresh at the start of the
 * beat, and once more at its end - one timer, nothing polls.
 */
function useBeatClock(cutAt) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (cutAt === null) return undefined;
    setNow(Date.now());
    const left = cutAt + TITLE_CARD_MS - Date.now();
    if (left <= 0) return undefined;
    const timer = setTimeout(() => setNow(Date.now()), left + 10);
    return () => clearTimeout(timer);
  }, [cutAt]);
  return now;
}
